// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Box, Stack } from '@mui/material';
import type { ReactNode } from 'react';

interface DetailPageBodyProps {
  /**
   * Cards for the right gutter. Wrap each card in a
   * `<Box sx={{ flex: 1, minWidth: 0 }}>` so cards share the row evenly
   * when the gutter wraps below the main column under the md breakpoint
   */
  side: ReactNode;

  /**
   * Content of the main column, stacked with the standard spacing
   */
  children: ReactNode;
}

/**
 * The standard body layout for entity detail pages: a main column beside a
 * fixed-width right gutter. The gutter collapses below the main content on
 * viewports narrower than the md breakpoint.
 */
export function DetailPageBody(props: DetailPageBodyProps) {
  return (
    <Box
      sx={{
        alignItems: 'flex-start',
        display: 'grid',
        gap: 2,
        gridTemplateAreas: {
          xs: `
            "main"
            "side"
          `,
          sm: `
            "main main"
            "side side"
          `,
          md: `
            "main side"
          `,
        },
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'minmax(0, 1fr) minmax(0, 1fr)',
          md: 'minmax(0, 7fr) minmax(0, 360px)',
        },
        marginY: 3,
        paddingX: { xs: 1, md: 3 },
      }}
    >
      <Stack spacing={2} sx={{ gridArea: 'main', minWidth: 0 }}>
        {props.children}
      </Stack>
      <Box sx={{ gridArea: 'side', minWidth: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row', md: 'column' }}
          spacing={3}
          sx={{ alignItems: 'stretch' }}
        >
          {props.side}
        </Stack>
      </Box>
    </Box>
  );
}
